import { useState } from 'react';
import { FileText, Clock, CheckCircle2 } from 'lucide-react';

interface Contract {
  id: string;
  title: string;
  client: string;
  date: string;
  amount: string;
}

export interface ContractsData {
  draft: Contract[];
  pending: Contract[];
  signed: Contract[];
}

interface ContractsOverviewProps {
  contracts?: ContractsData;
}

export function ContractsOverview({ contracts }: ContractsOverviewProps) {
  const [activeTab, setActiveTab] = useState<'draft' | 'pending' | 'signed'>('pending');

  const defaultContracts: ContractsData = {
    draft: [
      { id: '1', title: 'Basement Finishing Contract', client: 'Emily Davis', date: 'Jan 5, 2026', amount: '$28,500' },
      { id: '2', title: 'Garage Addition', client: 'Tom Anderson', date: 'Jan 7, 2026', amount: '$42,000' }
    ],
    pending: [
      { id: '3', title: 'Kitchen Remodel Agreement', client: 'John Smith', date: 'Jan 3, 2026', amount: '$35,000' },
      { id: '4', title: 'Bathroom Renovation', client: 'Sarah Johnson', date: 'Jan 4, 2026', amount: '$18,500' },
      { id: '5', title: 'Window Installation Contract', client: 'Lisa Martinez', date: 'Jan 6, 2026', amount: '$12,800' }
    ],
    signed: [
      { id: '6', title: 'Deck Construction Contract', client: 'Michael Chen', date: 'Dec 28, 2025', amount: '$22,000' },
      { id: '7', title: 'Roof Replacement Agreement', client: 'Robert Wilson', date: 'Dec 30, 2025', amount: '$31,500' },
      { id: '8', title: 'Fence Installation', client: 'Patricia Brown', date: 'Jan 2, 2026', amount: '$8,900' },
      { id: '9', title: 'HVAC System Upgrade', client: 'James Taylor', date: 'Jan 4, 2026', amount: '$15,600' }
    ]
  };

  const displayContracts = contracts ?? defaultContracts;

  const tabs = [
    { key: 'draft' as const, label: 'Draft', icon: FileText, count: displayContracts.draft.length },
    { key: 'pending' as const, label: 'Awaiting Signature', icon: Clock, count: displayContracts.pending.length },
    { key: 'signed' as const, label: 'Signed', icon: CheckCircle2, count: displayContracts.signed.length }
  ];

  function ContractRow({ contract }: { contract: Contract }) {
    return (
      <div className="flex items-center justify-between py-3.5 px-4 hover:bg-gray-50 rounded-lg transition-all duration-150 border-b border-gray-100 last:border-0 cursor-pointer group">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-gray-900 text-sm group-hover:text-indigo-600 transition-colors">{contract.title}</div>
          <div className="text-xs text-gray-600 mt-1">{contract.client}</div>
        </div>
        <div className="flex items-center gap-8">
          <div className="text-xs text-gray-500 font-medium">{contract.date}</div>
          <div className="font-bold text-gray-900 w-20 text-right">{contract.amount}</div>
          <button className="text-indigo-600 hover:text-indigo-700 text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity">
            View
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-panel contracts-panel bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
      <h3 className="font-[500] text-gray-900 mb-5 tracking-tight">Contracts</h3>
      
      <div className="contractor-contracts__tabs mb-6">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`contractor-contracts__tab flex items-center justify-center gap-2 px-4 py-2.5 transition-all duration-200 flex-1 ${
                activeTab === tab.key
                  ? 'contractor-contracts__tab--active'
                  : ''
              }`}
            >
              <Icon className="w-4 h-4" />
              <span className="font-[500] text-sm">{tab.label}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                activeTab === tab.key
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'bg-gray-200 text-gray-700'
              }`}>
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>

      <div className="space-y-0">
        {displayContracts[activeTab].map((contract) => (
          <ContractRow key={contract.id} contract={contract} />
        ))}
      </div>
    </div>
  );
}
